<template>
  <div class="user-input-container">
    <div
      v-if="allowDocumentTextSelection && documentSelectedText"
      class="selected-text"
    >
      <div class="display">
        <AppIcon name="copy-text" class-name="flex-0" />
        <p class="body s bold flex-0">
          {{ t('legal_assistant.common.selected_text') }}
        </p>
        <p class="body s flex-1">{{ documentSelectedText }}</p>
        <div
          role="button"
          class="flex-0 display-flex align-center"
          @click="clearDocumentSelectedText"
        >
          <AppIcon name="X" class-name="clickable" />
        </div>
      </div>
    </div>
    <div class="user-input-area">
      <div
        v-if="showTextSelectionToggle"
        class="use-selected-text-wrapper"
        @click="toggleUseSelectedText"
      >
        <CustomCheckbox
          :modelValue="useDocumentSelectedText"
          :disabled="isDisabled"
        />
        <p class="body s font-regular text-align-left">
          {{
            t(
              `legal_assistant.actions.use_selected_text${
                OfficeUtils.isOutlook() ? '.outlook' : ''
              }`
            )
          }}
        </p>
      </div>
      <textarea
        ref="textarea"
        v-model="internalValue"
        :placeholder="placeholder ?? $t('legal_assistant.textarea.placeholder')"
        :disabled="isDisabled"
        @input="adjustHeight()"
        @keydown.enter.prevent="sendText()"
      ></textarea>
      <div class="action-icons">
        <AppIcon
          name="send"
          size="body-xl"
          class-name="color-lighter"
          :onclick="sendText"
          :clickable="!isDisabled"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import CustomCheckbox from '@/components/common/checkbox/CustomCheckbox.vue';
import { ErrorWithToastData } from '@/exceptions/ErrorWithToastData';
import { useToasterStore } from '@/stores/useToasterStore';
import { ChatUtils } from '@/utils/chat.utils';
import { OfficeUtils } from '@/utils/office.utils';
import {
  computed,
  defineEmits,
  defineProps,
  onMounted,
  onUnmounted,
  ref,
  watch,
  watchEffect
} from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  value?: string;
  placeholder?: string;
  onSend: (text: string) => Promise<void> | void;
  disabled?: boolean;
  allowDocumentTextSelection?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  allowDocumentTextSelection: false
});

onMounted(() => {
  if (props.value) {
    internalValue.value = props.value;
  }
});

const emit = defineEmits(['update:value']);
const useDocumentSelectedText = ref<boolean>(false);
const documentSelectedText = ref<string | undefined>();

const { t } = useI18n();
const toaster = useToasterStore();
const internalValue = ref(props.value || '');
const loading = ref(false);
const selecting = ref(false);

const isDisabled = computed(
  () => loading.value || props.disabled || selecting.value
);

const showTextSelectionToggle = computed(
  () => props.allowDocumentTextSelection && !OfficeUtils.isOutlook()
);

if (props.allowDocumentTextSelection) {
  onMounted(() => {
    OfficeUtils.startDocumentSelectionListener(selectionChangedHandler);
  });

  onUnmounted(() => {
    OfficeUtils.stopDocumentSelectionListener(selectionChangedHandler);
  });

  watchEffect(() => {
    if (useDocumentSelectedText.value) {
      selectionChangedHandler();
    }
  });
}

async function selectionChangedHandler(): Promise<void> {
  if (useDocumentSelectedText.value) {
    try {
      selecting.value = true;
      let text = await OfficeUtils.getSelectedText();
      if (!text) return;
      const words = text.split(' ');

      if (words.length > 5) {
        toaster.warning({
          title: t('legal_assistant.common.selected_text_warning_title'),
          body: t('legal_assistant.common.selected_text_warning_body')
        });

        text = words.slice(0, 5).join(' ') + '...';
      }

      documentSelectedText.value = text;
    } catch (error) {
      console.error('Error updating selected text:', error);
    } finally {
      selecting.value = false;
    }
  }
}

async function sendText() {
  if (isDisabled.value || !internalValue.value.trim()) return;
  loading.value = true;
  try {
    let valueToSend = internalValue.value.trim();

    if (props.allowDocumentTextSelection && useDocumentSelectedText.value) {
      valueToSend = ChatUtils.formatSelectedTextAndMessage(
        documentSelectedText.value || '',
        valueToSend
      );
    }

    clearText();
    await props.onSend(valueToSend);
  } catch (error) {
    if (error instanceof ErrorWithToastData)
      toaster.error({ title: error.title, body: error.body });

    console.error('Error sending text:', error);
  } finally {
    loading.value = false;
  }
}

function clearText() {
  internalValue.value = '';
  clearDocumentSelectedText();
  adjustHeight(true);
}

function clearDocumentSelectedText(): void {
  documentSelectedText.value = undefined;
  useDocumentSelectedText.value = false;
  OfficeUtils.clearTextSelection();
}

function toggleUseSelectedText() {
  if (loading.value || props.disabled) return;
  useDocumentSelectedText.value = !useDocumentSelectedText.value;
}

const textarea = ref<HTMLTextAreaElement | null>(null);

function adjustHeight(resetToMin = false) {
  if (!textarea.value) return;
  const cs = window.getComputedStyle(textarea.value);
  if (resetToMin) {
    textarea.value.style.height =
      cs.minHeight !== '0px' ? cs.minHeight : 'auto';
    textarea.value.style.overflowY = 'hidden';
  } else {
    textarea.value.style.height = 'auto';
    const sh = textarea.value.scrollHeight;
    const maxH = parseInt(cs.maxHeight, 10);
    textarea.value.style.height = `${Math.min(sh, maxH)}px`;
    textarea.value.style.overflowY = sh > maxH ? 'auto' : 'hidden';
  }
}

watch(
  () => props.value,
  (newVal) => {
    internalValue.value = newVal || '';
  }
);

watch(internalValue, (newVal) => {
  emit('update:value', newVal);
});
</script>

<style lang="scss" scoped>
.user-input-container {
  position: relative;
  bottom: 0;
  left: 0;
  right: 0;
  padding-top: $space-2;
  background: $chat-input-container-background;
  width: 100%;
}

.user-input-area {
  border: $border;
  border-radius: $radius-5;
  background-color: $color-neutral-0;
  padding: $space-2;
  min-height: 10vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.user-input-area .action-icons {
  display: flex;
  justify-content: flex-end;
}

.selected-text {
  position: absolute;
  top: -$space-6;
  z-index: 10;
  width: 100%;
  display: flex;
  justify-content: center;

  .display {
    border-radius: $radius-3;
    background-color: $color-neutral-900;
    color: $color-text-white;
    padding: $space-1 $space-2;
    display: flex;
    gap: $space-1;
    max-width: 85vw;
    align-items: center;

    p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      user-select: none;
    }

    .flex-0 {
      flex: 0 0 auto;
    }

    .flex-1 {
      flex: 1 1 auto;
    }
  }

  .remove {
    cursor: pointer;

    &:hover {
      transition: 0.2s;
      box-shadow: $elevation-3;
      transform: translateY(-1.5px);
    }
  }
}

.use-selected-text-wrapper {
  background-color: $color-neutral-500;
  padding: $space-1 $space-2;
  display: flex;
  flex-direction: row;
  gap: $space-2;
  border-radius: $radius-3;
  color: $color-text-white;
  flex-wrap: nowrap !important;
  cursor: pointer;

  > * {
    align-self: center;
    user-select: none;
  }
}

textarea::placeholder {
  user-select: none;
}

// Styles for big screens
@media (min-height: $small-screen-height) {
  .user-input-area {
    padding: $space-4 $space-3;
  }
}
</style>
